<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import ChannelIcon from 'dashboard/components-next/icon/ChannelIcon.vue';
import { getChannelSvgUrlForInbox } from 'dashboard/components-next/icon/channelSvgUrls';
import wootConstants from 'dashboard/constants/globals';
import AvatarInitial from './AvatarInitial.vue';

const props = defineProps({
  src: {
    type: String,
    default: '',
  },
  name: {
    type: String,
    required: true,
  },
  size: {
    type: Number,
    default: 32,
  },
  iconSize: {
    type: Number,
    default: null,
  },
  allowUpload: {
    type: Boolean,
    default: false,
  },
  roundedFull: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: null,
    validator: value =>
      !value || wootConstants.AVAILABILITY_STATUS_KEYS.includes(value),
  },
  inbox: {
    type: Object,
    default: null,
  },
  useColoredChannelIcon: {
    type: Boolean,
    default: false,
  },
  iconName: {
    type: String,
    default: null,
  },
  emoji: {
    type: String,
    default: '',
  },
  hideOfflineStatus: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['upload', 'delete']);

const { t } = useI18n();

const isImageValid = ref(true);
const fileInput = ref(null);
const BRAND_FALLBACK_CLASSES =
  "before:absolute before:inset-0 before:-z-10 before:bg-[var(--initial-loader-brand,#2781F6)] before:opacity-10 before:content-[''] [color:var(--initial-loader-brand,#2781F6)]";
const INITIAL_COLORS = [
  'bg-avatar-orange text-black',
  'bg-avatar-purple text-white',
  'bg-avatar-pink text-white',
  'bg-avatar-rose text-white',
  'bg-avatar-amber text-black',
  'bg-avatar-lime text-black',
  'bg-avatar-emerald text-black',
  'bg-avatar-teal text-black',
  'bg-avatar-sky text-black',
  'bg-avatar-blue text-white',
];
const normalizedName = computed(() =>
  (props.name || '').trim().normalize('NFC')
);
const initial = computed(() => {
  const letter = normalizedName.value.match(/[\p{L}\p{N}]/u)?.[0] || '';
  return Array.from(letter.toLocaleUpperCase())[0] || '';
});
const showInitial = computed(
  () =>
    (!props.src || !isImageValid.value) &&
    !props.emoji &&
    !props.iconName &&
    !!initial.value
);
const initialColor = computed(() => {
  const hash = Array.from(normalizedName.value.toLowerCase()).reduce(
    (value, character) => (value * 31 + character.codePointAt(0)) % 2 ** 32,
    0
  );
  return INITIAL_COLORS[hash % INITIAL_COLORS.length];
});
const initialSizeClass = computed(() => {
  if (props.size <= 20) return 'text-xs';
  if (props.size <= 24) return 'text-sm';
  if (props.size <= 32) return 'text-base';
  if (props.size <= 40) return 'text-lg';
  if (props.size <= 48) return 'text-2xl';
  if (props.size <= 64) return 'text-3xl';
  if (props.size <= 96) return 'text-5xl';
  return 'text-6xl';
});

const STATUS_CLASSES = computed(() => ({
  online: 'bg-n-teal-10',
  busy: 'bg-n-amber-10',
  break: 'bg-n-violet-10',
  ...(props.hideOfflineStatus ? {} : { offline: 'bg-n-slate-10' }),
}));

const showDefaultAvatar = computed(() => !props.src && !props.name);

const containerStyles = computed(() => ({
  width: `${props.size}px`,
  height: `${props.size}px`,
}));

const borderRadiusClass = computed(() => {
  if (props.roundedFull) {
    return 'rounded-full';
  }

  // Approximates 25% of size
  if (props.size <= 16) return 'rounded'; // 4px
  if (props.size <= 24) return 'rounded-md'; // 6px
  if (props.size <= 32) return 'rounded-lg'; // 8px
  if (props.size <= 48) return 'rounded-xl'; // 12px
  return 'rounded-2xl'; // 16px
});

const avatarStyles = computed(() => containerStyles.value);

const badgeStyles = computed(() => {
  const badgeSize = Math.max(props.size * 0.35, 8); // 35% of avatar size, minimum 8px
  return {
    width: `${badgeSize}px`,
    height: `${badgeSize}px`,
    top: `${props.size - badgeSize / 1.1}px`,
    left: `${props.size - badgeSize / 1.1}px`,
  };
});

/** Inbox/channel icon beside avatar (e.g. message bubbles); fixed readable size */
const INBOX_CHANNEL_BADGE_PX = 17;

const inboxBadgeStyles = computed(() => {
  const badgeSize = INBOX_CHANNEL_BADGE_PX;
  return {
    width: `${badgeSize}px`,
    height: `${badgeSize}px`,
    top: `${props.size - badgeSize / 1.1}px`,
    left: `${props.size - badgeSize / 1.1}px`,
  };
});

const channelSvgUrl = computed(() => {
  if (!props.useColoredChannelIcon || !props.inbox) return '';
  return getChannelSvgUrlForInbox(props.inbox) || '';
});

const iconStyles = computed(() => ({
  fontSize: `${props.iconSize || props.size / 1.6}px`,
}));

const invalidateCurrentImage = () => {
  isImageValid.value = false;
};

const handleUploadAvatar = () => {
  fileInput.value.click();
};

const handleImageUpload = event => {
  const [file] = event.target.files;
  if (file) {
    emit('upload', {
      file,
      url: file ? URL.createObjectURL(file) : null,
    });
  }
};

const handleDeleteAvatar = () => {
  if (fileInput.value) {
    fileInput.value.value = null;
  }
  emit('delete');
};

const handleDismiss = event => {
  event.stopPropagation();
  handleDeleteAvatar();
};

watch(
  () => props.src,
  () => {
    isImageValid.value = true;
  }
);
</script>

<template>
  <span
    class="relative inline-flex group/avatar z-0 flex-shrink-0 align-middle"
    :style="containerStyles"
  >
    <!-- Status Badge -->
    <slot name="badge" :size="size">
      <div
        v-if="status && STATUS_CLASSES[status]"
        class="absolute z-20 border rounded-full border-n-slate-3"
        :style="badgeStyles"
        :class="STATUS_CLASSES[status]"
      />
      <div
        v-if="inbox && !(status && STATUS_CLASSES[status])"
        :style="inboxBadgeStyles"
        class="absolute z-20 flex items-center justify-center rounded-full bg-n-background flex-shrink-0"
      >
        <img
          v-if="channelSvgUrl"
          :src="channelSvgUrl"
          alt=""
          aria-hidden="true"
          class="size-[13px] shrink-0 object-contain"
        />
        <ChannelIcon
          v-else
          :inbox="inbox"
          class="size-[13px] shrink-0 text-n-slate-11"
        />
      </div>
    </slot>

    <!-- Delete Avatar Button -->
    <div
      v-if="src && allowUpload"
      class="absolute z-20 flex items-center justify-center invisible w-6 h-6 transition-all duration-300 ease-in-out opacity-0 cursor-pointer outline outline-1 outline-n-container -top-2 ltr:-right-2 rtl:-left-2 rounded-xl bg-n-solid-3 group-hover/avatar:visible group-hover/avatar:opacity-100"
      @click="handleDismiss"
    >
      <Icon icon="i-lucide-x" class="text-n-slate-11 size-4" />
    </div>

    <!-- Avatar Container -->
    <span
      role="img"
      :aria-label="name || t('THUMBNAIL.AUTHOR.NOT_AVAILABLE')"
      class="relative isolate inline-flex items-center justify-center object-cover overflow-hidden font-medium outline outline-1 -outline-offset-1 outline-[rgb(0_0_0_/_0.03)] dark:outline-[rgb(255_255_255_/_0.04)]"
      :class="[
        borderRadiusClass,
        showInitial ? initialColor : '',
        !showInitial && !showDefaultAvatar && (!src || !isImageValid)
          ? BRAND_FALLBACK_CLASSES
          : '',
        {
          'bg-n-slate-3 dark:bg-n-slate-4': showDefaultAvatar,
        },
      ]"
      :style="avatarStyles"
    >
      <!-- Avatar Content -->
      <img
        v-if="src && isImageValid"
        :src="src"
        :alt="name"
        @error="invalidateCurrentImage"
      />

      <template v-else>
        <span v-if="emoji" class="text-lg leading-none" aria-hidden="true">
          {{ emoji }}
        </span>

        <!-- Custom Icon -->
        <Icon v-else-if="iconName" :icon="iconName" :style="iconStyles" />

        <AvatarInitial
          v-else-if="showInitial"
          :initial="initial"
          :size-class="initialSizeClass"
          class="select-none font-semibold leading-none"
          aria-hidden="true"
        />

        <!-- Fallback for unnamed users -->
        <Icon
          v-else
          :title="t('THUMBNAIL.AUTHOR.NOT_AVAILABLE')"
          icon="i-lucide-user"
          :style="iconStyles"
        />
      </template>

      <!-- Upload Overlay and Input -->
      <slot
        v-if="allowUpload || $slots.overlay"
        name="overlay"
        :size="size"
        :handle-upload="handleUploadAvatar"
        :file-input-ref="fileInput"
        :handle-image-upload="handleImageUpload"
      >
        <div
          class="absolute inset-0 z-10 flex items-center justify-center invisible w-full h-full transition-all duration-300 ease-in-out opacity-0 bg-n-alpha-black1 group-hover/avatar:visible group-hover/avatar:opacity-100"
          :class="borderRadiusClass"
          @click="handleUploadAvatar"
        >
          <Icon
            icon="i-lucide-upload"
            class="text-white"
            :style="{ width: `${size / 2}px`, height: `${size / 2}px` }"
          />
          <input
            v-if="allowUpload"
            ref="fileInput"
            type="file"
            accept="image/png, image/jpeg, image/jpg, image/gif, image/webp"
            class="hidden"
            @change="handleImageUpload"
          />
        </div>
      </slot>
    </span>
  </span>
</template>
